import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Box, Stack, Typography, Paper, Divider } from '@mui/material';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import { Formik, FormikProps } from 'formik';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { AddNewPersonValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/PersonTableValidationSchema';
import { PersonUsertRequest } from 'types/api/TenantMaster/MasterTable/PersonTableDataTypes';
import { valueSafeEcryptor } from 'utils/Encryptor';
import { useCreatePerson } from 'services/v1/TenantMaster/MasterTable/PersonTableDataService';
import { PATH_CONSTANT } from 'constant/PathConstant';

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

interface FormValue {
  person: string;
  fullName: string;
  friendlyName: string;
  mobile: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const formInitialValues: FormValue = {
  person: '',
  fullName: '',
  friendlyName: '',
  mobile: '',
  confirmPassword: '',
  password: '',
  email: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

function StepIndicator({ step, total, active }: { step: number; total: number; active: boolean }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1} mb={2}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: active ? '#3B4797' : '#E3EBF6',
          color: active ? '#fff' : '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {step}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
        Step {step} of {total}
      </Typography>
    </Stack>
  );
}

export default function AddPersonTablePage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const formikRef = useRef<FormikProps<FormValue>>(null);
  const navigate = useNavigate();
  const { mutate: addPerson, isLoading: isInserting } = useCreatePerson();

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordToggle = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleOnSave = (data: FormValue) => {
    const payload: PersonUsertRequest = {
      fullName: data.fullName,
      friendlyName: data.friendlyName,
      mobile: data.mobile,
      userName: data.email,
      password: valueSafeEcryptor(data.confirmPassword) || '',
      person: data.person,
    };

    addPerson(payload, {
      onSuccess: () => {
        toast.success('Data submitted successfully!');
        formikRef.current?.resetForm();
        handleOnCancelInput();
      },
    });
  };

  const handleOnCancelInput = () => {
    navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.PERSON);
  };

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        <Formik
          innerRef={formikRef}
          initialValues={formInitialValues}
          validationSchema={AddNewPersonValidationSchema}
          onSubmit={handleOnSave}
        >
          {(formikProps: FormikProps<{ [key: string]: string }>) => {
            const { values, handleChange, errors, touched, handleBlur } = formikProps;
            return (
              <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                {/* Page Header */}
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                  <Box>
                    <Typography
                      variant='body1'
                      component='h2'
                      fontWeight='bold'
                      sx={{ color: '#3B4797', fontSize: 18 }}
                    >
                      Add New Person
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                      Create a new user account by providing their personal details and login credentials below. Fields
                      marked with <span style={{ color: '#EF4444' }}>*</span> are required.
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    onClick={handleOnCancelInput}
                    variant='main-table-panel-border'
                    disabled={isInserting}
                  >
                    Cancel
                  </Button>
                </Stack>

                {/* Section 1: Personal Information */}
                <Paper sx={sectionPaperStyles}>
                  <StepIndicator step={1} total={3} active={true} />
                  <SectionHeader
                    title='Personal Information'
                    description='Enter the person identifier and their name. The person field is a unique code used internally.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <FormField label='Person' required>
                    <TextField
                      fullWidth
                      size='small'
                      disabled={isInserting}
                      placeholder='e.g. john.doe — a unique identifier for this person'
                      onChange={handleChange}
                      value={values?.person}
                      onBlur={handleBlur}
                      name='person'
                      error={touched.person && Boolean(errors.person)}
                      helperText={touched.person && errors.person}
                    />
                  </FormField>

                  <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        First Name <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isInserting}
                        placeholder='e.g. John'
                        onChange={handleChange}
                        value={values?.fullName}
                        onBlur={handleBlur}
                        name='fullName'
                        error={touched.fullName && Boolean(errors.fullName)}
                        helperText={touched.fullName && errors.fullName}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>Family Name</Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isInserting}
                        placeholder='e.g. Doe'
                        onChange={handleChange}
                        value={values?.friendlyName}
                        onBlur={handleBlur}
                        name='friendlyName'
                        error={touched.friendlyName && Boolean(errors.friendlyName)}
                        helperText={touched.friendlyName && errors.friendlyName}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {/* Section 2: Contact Details */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={2} total={3} active={!!values.person} />
                  <SectionHeader
                    title='Contact Details'
                    description='Provide the email address and mobile number. The email will be used as the login username.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Email <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isInserting}
                        placeholder='e.g. johndoe@email.com'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.email}
                        name='email'
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Mobile Phone <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isInserting}
                        placeholder='e.g. 6123456789'
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.match(/^[0-9]*$/)) {
                            handleChange(e);
                          }
                        }}
                        onBlur={handleBlur}
                        value={values?.mobile}
                        name='mobile'
                        error={touched.mobile && Boolean(errors.mobile)}
                        helperText={touched.mobile && errors.mobile}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {/* Section 3: Password */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={3} total={3} active={!!values.email} />
                  <SectionHeader
                    title='Login Credentials'
                    description='Set the initial password for this user. They can change it after their first login.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Password <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isInserting}
                        type={showPassword ? 'text' : 'password'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Enter password'
                        value={values?.password}
                        name='password'
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton onClick={handlePasswordToggle} size='small'>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Confirm Password <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        type={showConfirmPassword ? 'text' : 'password'}
                        disabled={isInserting}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Re-enter password'
                        value={values?.confirmPassword}
                        name='confirmPassword'
                        error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                        helperText={touched.confirmPassword && errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton onClick={handleConfirmPasswordToggle} size='small'>
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {/* Action Buttons */}
                <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                  <Button disabled={isInserting} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                    Cancel
                  </Button>
                  <ButtonLoading
                    loading={isInserting}
                    variant='main-table-panel'
                    onClick={() => formikProps.handleSubmit()}
                    // @ts-ignore
                    disabled={!AddNewPersonValidationSchema?.isValidSync(formikProps.values) || isInserting}
                  >
                    Create Person
                  </ButtonLoading>
                </Stack>
              </Box>
            );
          }}
        </Formik>
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
